"use client";

import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import OpportunitiesList from "./OpportunitiesList";
import OpportunityDetails from "./OpportunityDetails";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

export default function OpportunitiesTab() {
  const navigate = useNavigate();
  const { opportunityId } = useParams();

  const [opportunities, setOpportunities] = useState([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoadingOpportunity, setIsLoadingOpportunity] = useState(false);
  const [opportunityError, setOpportunityError] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");

  // Memoize filtered lists so they aren't re-derived on every render
  const filteredUsers = useMemo(
    () =>
      users.filter((u) => u.role === "project_manager" || u.role === "admin"),
    [users],
  );
  const filteredAccounts = useMemo(
    () => accounts.filter((a) => a.type === "account"),
    [accounts],
  );

  // Initial data fetch — proposals fetched once here, filtered client-side in detail view
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const decoded = jwtDecode(token);
        setUserEmail(decoded.email || "");

        const headers = { Authorization: `Bearer ${token}` };
        const [
          oppsRes,
          proposalsRes,
          usersRes,
          accountsRes,
          contactsRes,
          profileRes,
        ] = await Promise.all([
          axios.get(`${API}/opportunities`, { headers }),
          axios.get(`${API}/proposals`, { headers }),
          axios.get(`${API}/users`, { headers }),
          axios.get(`${API}/accounts`, { headers }),
          axios.get(`${API}/contacts`, { headers }),
          axios.get(`${API}/company-profile`, { headers }),
        ]);

        const fetchedOpps = Array.isArray(oppsRes.data?.opportunities)
          ? oppsRes.data.opportunities
          : Array.isArray(oppsRes.data)
            ? oppsRes.data
            : [];

        setOpportunities(fetchedOpps);
        setProposals(proposalsRes.data || []);
        setUsers(usersRes.data || []);
        setAccounts(accountsRes.data || []);
        setContacts(contactsRes.data || []);
        setCompanyProfile(profileRes.data);
      } catch (err) {
        setError(
          "Failed to load data: " +
            (err.response?.data?.message || err.message),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Resolve selected opportunity — prefer cached list entry, only fetch if missing
  useEffect(() => {
    if (!opportunityId) {
      setSelectedOpportunity(null);
      setOpportunityError(null);
      return;
    }

    // Avoid acting before the initial fetch has completed
    if (loading) return;

    const cached = opportunities.find((o) => o._id === opportunityId);
    if (cached) {
      setSelectedOpportunity(cached);
      setOpportunityError(null);
      return;
    }

    // Not in cache — fetch individually (e.g. direct URL load before list was ready,
    // or an ID that isn't in the paginated list)
    const fetchOne = async () => {
      setIsLoadingOpportunity(true);
      setOpportunityError(null);
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const res = await axios.get(`${API}/opportunities/${opportunityId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data?._id) {
          setSelectedOpportunity(res.data);
          setOpportunities((prev) => {
            const exists = prev.some((o) => o._id === res.data._id);
            return exists
              ? prev.map((o) => (o._id === res.data._id ? res.data : o))
              : [...prev, res.data];
          });
        } else {
          throw new Error("Opportunity not found");
        }
      } catch (err) {
        setOpportunityError(
          err.response?.data?.message || err.message || "Opportunity not found",
        );
        setSelectedOpportunity(null);
      } finally {
        setIsLoadingOpportunity(false);
      }
    };

    fetchOne();
  }, [opportunityId, loading]); // `opportunities` intentionally omitted — we only
  // want to re-run when the ID changes or load completes,
  // not every time the list updates

  const handleSort = () =>
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));

  // Proposals for the detail view — filter client-side from the already-fetched set
  const opportunityProposals = useMemo(
    () =>
      proposals.filter(
        (p) => (p.opportunity?._id ?? p.opportunity) === opportunityId,
      ),
    [proposals, opportunityId],
  );

  if (loading) return <div className="text-center py-4">Loading...</div>;
  if (error)
    return <div className="text-red-600 text-center py-4">{error}</div>;
  if (!Array.isArray(opportunities))
    return (
      <div className="text-red-600 text-center py-4">
        Error: Opportunities data is not available
      </div>
    );

  return (
    <div className="max-w-full mx-auto px-4 py-6">
      {opportunityId ? (
        isLoadingOpportunity ? (
          <div className="text-center py-4">Loading opportunity details...</div>
        ) : opportunityError || !selectedOpportunity ? (
          <div className="text-red-600 text-center py-4">
            {opportunityError || "Opportunity not found"}
            <div className="mt-4">
              <button
                onClick={() => navigate("/Opportunities")}
                className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500 ml-2"
              >
                Back to Opportunities
              </button>
            </div>
          </div>
        ) : (
          <OpportunityDetails
            opportunity={selectedOpportunity}
            proposals={opportunityProposals}
            setProposals={setProposals}
            setOpportunities={(updaterOrValue) => {
              setOpportunities((prev) => {
                const next =
                  typeof updaterOrValue === "function"
                    ? updaterOrValue(prev)
                    : updaterOrValue;
                const updated = next.find(
                  (o) => o._id === selectedOpportunity?._id,
                );
                if (updated) setSelectedOpportunity(updated);
                return next;
              });
            }}
            users={filteredUsers}
            accounts={filteredAccounts}
            contacts={contacts}
            companyProfile={companyProfile}
            userEmail={userEmail}
            navigate={navigate}
          />
        )
      ) : (
        <OpportunitiesList
          opportunities={opportunities}
          setOpportunities={setOpportunities}
          sortOrder={sortOrder}
          handleSort={handleSort}
          navigate={navigate}
          users={filteredUsers}
          accounts={filteredAccounts}
          contacts={contacts}
        />
      )}
    </div>
  );
}
